/**
 * The subset of markdown a help body uses, rendered to HTML — headings, paragraphs, ordered and
 * unordered lists, bold, inline code and links. Nothing else, on purpose: a help entry that needs
 * more than this is a page, not a help entry (HELP_SYSTEM_SPEC §4). Every text node is escaped, so
 * a body can quote `<HelpBadge>` without becoming one.
 */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

function inline(text: string): string {
  let out = escapeHtml(text);
  out = out.replace(/`([^`]+)`/g, "<code>$1</code>");
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Links keep to http(s) and same-site paths; anything else stays as text.
  out = out.replace(/\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g, '<a href="$2">$1</a>');
  return out;
}

export function renderMarkdown(body: string): string {
  const lines = body.split("\n");
  const html: string[] = [];
  let list: "ol" | "ul" | null = null;
  let para: string[] = [];
  const flushPara = () => { if (para.length > 0) { html.push(`<p>${inline(para.join(" "))}</p>`); para = []; } };
  const closeList = () => { if (list !== null) { html.push(`</${list}>`); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    const ordered = line.match(/^\d+\.\s+(.*)$/);
    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (line.trim() === "") { flushPara(); closeList(); continue; }
    if (heading) { flushPara(); closeList(); const n = Math.min(heading[1]!.length + 1, 6); html.push(`<h${n}>${inline(heading[2]!)}</h${n}>`); continue; }
    if (ordered || bullet) {
      flushPara();
      const kind = ordered ? "ol" : "ul";
      if (list !== kind) { closeList(); html.push(`<${kind}>`); list = kind; }
      html.push(`<li>${inline((ordered ?? bullet)![1]!)}</li>`);
      continue;
    }
    closeList();
    para.push(line.trim());
  }
  flushPara(); closeList();
  return html.join("\n");
}
